import { useSeo } from '@/hooks/useSeo';

export default function PrivacyPage() {
  useSeo({
    title: 'Privacy Policy',
    description: "Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 (GDPR) — Ciro Gallotti, Napoli.",
    path: '/privacy',
  });

  return (
    <article className="bg-ivory pt-24 md:pt-28" data-testid="privacy-page">
      <header className="px-6 md:px-12 lg:px-16 py-12 md:py-20 hairline-b">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px bg-bordeaux" />
            <p className="text-eyebrow text-bordeaux">Informativa</p>
          </div>
          <h1 className="font-display text-h1 text-ink">
            Privacy <span className="italic">Policy</span>
          </h1>
          <p className="mt-6 text-ink/65 leading-relaxed">
            Ultimo aggiornamento: febbraio 2026 — Regolamento (UE) 2016/679 (GDPR) e D.lgs. 196/2003.
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16 space-y-12 text-ink/80 leading-relaxed">
        <section>
          <h2 className="font-display text-h3 text-ink mb-4">1. Titolare del trattamento</h2>
          <p>
            Il titolare del trattamento dei dati personali è <strong>Ciro Gallotti</strong> (P.IVA 11025931210),
            con sede in Via Chiaia 138, 80121 Napoli (Italia). Per contatti relativi alla privacy:
            {' '}<a href="mailto:cirogallotti81@libero.it" className="text-bordeaux hover:underline">cirogallotti81@libero.it</a>.
          </p>
        </section>

        <section>
          <h2 className="font-display text-h3 text-ink mb-4">2. Dati raccolti e finalità</h2>
          <p>Trattiamo i dati personali che ci fornisci attivamente, esclusivamente per le finalità seguenti:</p>
          <ul className="mt-4 space-y-3 list-disc pl-6">
            <li><strong>Acquisto online</strong>: nome, email, telefono, indirizzo di spedizione, dati di fatturazione — base giuridica: esecuzione del contratto (art. 6.1.b GDPR).</li>
            <li><strong>Prenotazione appuntamento</strong>: nome, email, telefono, data preferita — base giuridica: esecuzione di misure precontrattuali su richiesta dell'interessato.</li>
            <li><strong>Form di contatto</strong>: nome, email, oggetto, messaggio — base giuridica: legittimo interesse a rispondere alle richieste.</li>
            <li><strong>Cookie analitici (PostHog)</strong>: id sessione anonimo, eventi di navigazione — base giuridica: consenso esplicito (art. 6.1.a GDPR), revocabile in ogni momento.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-h3 text-ink mb-4">3. Conservazione dei dati</h2>
          <p>
            I dati relativi agli ordini sono conservati per <strong>10 anni</strong> ai fini fiscali (art. 2220 c.c.).
            I dati di contatto e prenotazione sono conservati per il tempo strettamente necessario all'evasione
            della richiesta e poi per ulteriori 24 mesi. Gli eventi analytics sono conservati per 12 mesi.
          </p>
        </section>

        <section>
          <h2 className="font-display text-h3 text-ink mb-4">4. Destinatari e trasferimenti</h2>
          <p>I tuoi dati possono essere comunicati ai seguenti soggetti, esclusivamente per le finalità sopra indicate:</p>
          <ul className="mt-4 space-y-2 list-disc pl-6">
            <li><strong>Stripe Payments Europe Ltd.</strong> (Irlanda) — gestione pagamenti.</li>
            <li><strong>PostHog Inc.</strong> (USA) — analytics anonimi, solo previo consenso. Trasferimento basato su Standard Contractual Clauses (SCC).</li>
            <li>Corrieri di spedizione, commercialisti, autorità competenti su richiesta legale.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-h3 text-ink mb-4">5. Cookie</h2>
          <p>
            Utilizziamo cookie tecnici essenziali (necessari per il funzionamento del sito, del carrello e
            del checkout) e cookie analitici di PostHog, questi ultimi caricati <strong>solo dopo il tuo
            consenso esplicito</strong> espresso tramite il banner. Puoi revocare il consenso in qualunque
            momento cancellando i dati del sito dal tuo browser.
          </p>
        </section>

        <section>
          <h2 className="font-display text-h3 text-ink mb-4">6. I tuoi diritti</h2>
          <p>Ai sensi degli artt. 15-22 GDPR puoi in qualsiasi momento:</p>
          <ul className="mt-4 space-y-2 list-disc pl-6">
            <li>Accedere ai tuoi dati e ricevere una copia (diritto di accesso).</li>
            <li>Chiedere la rettifica o la cancellazione dei dati (diritto di rettifica e oblio).</li>
            <li>Opporti al trattamento o chiederne la limitazione.</li>
            <li>Ricevere i dati in formato strutturato (portabilità).</li>
            <li>Revocare il consenso ai cookie analitici.</li>
            <li>Presentare reclamo al Garante per la Protezione dei Dati Personali
              {' '}(<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-bordeaux hover:underline">garanteprivacy.it</a>).</li>
          </ul>
          <p className="mt-4">
            Per esercitare i tuoi diritti scrivi a
            {' '}<a href="mailto:cirogallotti81@libero.it" className="text-bordeaux hover:underline">cirogallotti81@libero.it</a>.
            Risponderemo entro 30 giorni.
          </p>
        </section>

        <section>
          <h2 className="font-display text-h3 text-ink mb-4">7. Sicurezza</h2>
          <p>
            Adottiamo misure tecniche e organizzative adeguate (cifratura HTTPS, accessi protetti,
            backup periodici) per garantire la sicurezza dei dati personali e prevenire trattamenti
            non autorizzati o accidentali.
          </p>
        </section>

        <section>
          <h2 className="font-display text-h3 text-ink mb-4">8. Modifiche</h2>
          <p>
            Questa informativa può essere aggiornata; ogni modifica sostanziale verrà comunicata
            tramite avviso sul sito. La versione corrente reca la data di "Ultimo aggiornamento"
            indicata in apertura.
          </p>
        </section>
      </div>
    </article>
  );
}
